import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useUserLiteDropdown, useViewAsUser } from '@/hooks';
import { useToken, sentenceCase } from '@/common/helper';
import {
  setPreviewUser,
  PreviewUserMetadata,
} from '@/redux-toolkit/slice/previewSlice';
import { RootState } from '@/redux-toolkit/store';
import TeamsTag from '../../TeamsTag';
import CustomShipmentText from '../../CustomShipmentText';
import EllipsisTooltip from '../../EllipsisTooltip';
import { UserData, UserTeam } from '@/apis/onboarding';
import styles from '../PreviewHeader.module.css';
import PreviewDropdown, {
  PreviewDropdownOptions,
} from '../PreviewDropdown/PreviewDropdown';

export interface RawTeamItem {
  id?: string;
  team_id?: string;
  name?: string;
  team_name?: string;
  role?: string;
  role_name?: string;
  color?: string;
}

export interface ParsedTeamRolePair {
  teamId: string;
  teamName: string;
  roleName: string;
  color: string;
}

export interface UserOptionItem {
  id?: string;
  name?: string;
  email?: string;
  teams?: RawTeamItem[] | UserData[] | unknown[];
  color?: string;
  permissions?: {
    is_create?: boolean;
    is_update?: boolean;
  };
  disabled?: boolean;
  [key: string]: unknown;
}

/** Render user chip + team tags + overflow dots tooltip */
export const renderUserOptionContent = (
  item: UserOptionItem,
  isSelected?: boolean
) => {
  const userName =
    item?.name ||
    ('email' in item && typeof item.email === 'string' ? item.email : 'User');
  const userTeams: RawTeamItem[] = Array.isArray(item?.teams)
    ? (item.teams as RawTeamItem[])
    : [];
  const rest = userTeams.length > 2 ? userTeams.slice(2) : [];
  const teams = userTeams.length > 0 ? userTeams.slice(0, 2) : [];

  return (
    <div className={styles.teamsTagWrapper}>
      {/* User Chip */}
      <div
        className={`${styles.userChip} ${isSelected ? styles.userChipSelected : ''}`.trim()}
        data-testid='user-chip'
      >
        <EllipsisTooltip
          text={sentenceCase(userName)}
          className={`${styles.userChipText} ${isSelected ? styles.userChipTextSelected : ''}`.trim()}
          style={isSelected ? { color: '#000000', fontWeight: 600 } : undefined}
        />
      </div>
      <Flex align='center' style={{ minWidth: 0, overflow: 'hidden' }}>
        {teams.length > 0 && (
          <TeamsTag
            teamData={teams as unknown as Record<string, UserTeam>}
            border={false}
            padding={false}
            flexWrap='nowrap'
            highlightRole={isSelected}
            maxTruncate={10}
          />
        )}

        {rest.length > 0 && (
          <Tooltip
            title={
              <>
                {rest.map((t: RawTeamItem, index: number) => (
                  <div key={index}>
                    {t?.name || t?.team_name}: {t?.role_name || t?.role}
                  </div>
                ))}
              </>
            }
          >
            <div
              className='flex items-center gap-1 cursor-pointer teams-dots-wrapper'
              style={{ background: 'transparent', height: '20px' }}
            >
              <div className='teams-dots cursor-pointer'>
                {/* for color */}
                {rest.map((t: RawTeamItem, idx: number) => (
                  <span
                    key={idx}
                    className='dot'
                    style={{ backgroundColor: t?.color }}
                  />
                ))}
              </div>
              {/* +X counter */}
              <span className='ml-1'>+{rest.length}</span>
            </div>
          </Tooltip>
        )}
      </Flex>
    </div>
  );
};

export const ViewAsUserBanner: React.FC = () => {
  const dispatch = useDispatch();
  const { decodedUserToken } = useToken();
  const { handleViewAsUser } = useViewAsUser();

  const reduxPreviewUser = useSelector(
    (state: RootState) => state.preview?.previewUser
  );

  // User lite dropdown (GET /api/auth/users/ via useUserLiteDropdown)
  const {
    userList,
    loading: isUsersLoading,
    setSearch: setUsersSearch,
    search: userSearch,
    handlePopupScroll: handleUsersScroll,
  } = useUserLiteDropdown();

  const decoded = decodedUserToken();
  const currentUserId = String(
    decoded?.preview_target_user_id || decoded?.user_id || ''
  );

  // Resolve current user with 3-tier priority: Redux -> userList match -> Token fallback
  const resolvedUserItem: PreviewDropdownOptions | null = useMemo(() => {
    if (!currentUserId) return null;

    // 1. Redux store has the persisted user with full details and teams
    if (reduxPreviewUser && String(reduxPreviewUser.id) === currentUserId) {
      return reduxPreviewUser as unknown as PreviewDropdownOptions;
    }

    // 2. User is present in page 1 of userList
    const matchedInList = userList?.find((u) => String(u.id) === currentUserId);
    if (matchedInList) {
      // Cache to Redux for subsequent renders/refreshes
      dispatch(setPreviewUser(matchedInList as unknown as PreviewUserMetadata));
      return matchedInList as unknown as PreviewDropdownOptions;
    }

    // 3. Fallback from decoded token claims
    return {
      id: currentUserId,
      name: decoded?.name || decoded?.email || 'User',
      email: decoded?.email,
      teams: [],
    } as unknown as PreviewDropdownOptions;
  }, [
    currentUserId,
    reduxPreviewUser,
    userList,
    decoded?.name,
    decoded?.email,
    dispatch,
  ]);

  const [selectedUser, setSelectedUser] = useState<PreviewDropdownOptions[]>(
    () => (resolvedUserItem ? [resolvedUserItem] : [])
  );

  // Sync selected user when resolvedUserItem updates
  useEffect(() => {
    if (resolvedUserItem) {
      setSelectedUser([resolvedUserItem]);
    }
  }, [resolvedUserItem]);

  const handleUserChange = (users: PreviewDropdownOptions[]) => {
    const user = users[0];
    if (user && user.id) {
      handleViewAsUser(
        user as unknown as PreviewUserMetadata,
        true,
        false,
        true
      );
    }
  };

  const activeSelected =
    selectedUser.length > 0
      ? selectedUser
      : resolvedUserItem
        ? [resolvedUserItem]
        : [];

  return (
    <div className={styles.bannerContent} data-testid='view-as-user-banner'>
      <Flex align='center' gap={8}>
        <CustomShipmentText
          text={'Viewing as:'}
          lineHeight='20px'
          color={'#fff'}
        />

        <div data-testid='user-selector'>
          <PreviewDropdown
            options={userList as unknown as PreviewDropdownOptions[]}
            selectedOptions={activeSelected}
            onChange={handleUserChange}
            onSearch={setUsersSearch}
            optionsSearch={userSearch}
            showSearch={userSearch || userList.length > 10 ? true : false}
            onPopupScroll={handleUsersScroll}
            loading={isUsersLoading}
            singleSelect={true}
            className={`${styles.previewHeaderDd} ${styles.userDropdown}`}
            overlayClassName={styles.userPreviewOverlay}
            renderOption={(team, isSelected) =>
              renderUserOptionContent(team, isSelected)
            }
            renderTrigger={(selected) =>
              selected.length > 0
                ? renderUserOptionContent(selected[0], false)
                : null
            }
            showClearIcon={false}
          />
        </div>
      </Flex>
    </div>
  );
};

export default ViewAsUserBanner;
